// /opt/isp_erp/frontend/src/components/tickets/CreateTicketDialog.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { ticketsAPI } from '../../api/tickets.api';
import { getCustomers } from '../../api/clientes.api';

export default function CreateTicketDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    description: '',
    ticket_type: 'incidente',
    problem_origin: 'corte_total',
    priority: 'media',
    contact_phone: '',
    contact_phone_alt: ''
  });

  useEffect(() => {
    if (open) {
      loadCustomers();
      setSearchTerm('');
      setSelectedCustomer(null);
      setFormData({
        customer_id: '',
        title: '',
        description: '',
        ticket_type: 'incidente',
        problem_origin: 'corte_total',
        priority: 'media',
        contact_phone: '',
        contact_phone_alt: ''
      });
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.trim() && customers.length > 0) {
      const filtered = customers.filter(c => {
        const search = searchTerm.toLowerCase().trim();
        const nombreCompleto = `${c.nombres || ''} ${c.apellidos || ''}`.toLowerCase();
        const identificacion = (c.identificacion || '').toLowerCase();
        const telefono = (c.telefono || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        return nombreCompleto.includes(search) || 
               identificacion.includes(search) || 
               telefono.includes(search) || 
               email.includes(search);
      });
      setFilteredCustomers(filtered);
      setShowSearchResults(true);
    } else {
      setFilteredCustomers([]);
      setShowSearchResults(false);
    }
  }, [searchTerm, customers]);

  const loadCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError('Error al cargar clientes');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customer_id: customer.id,
      contact_phone: customer.telefono || customer.phone_mobile || '',
      contact_phone_alt: customer.phone_home || ''
    });
    setSearchTerm(`${customer.nombres} ${customer.apellidos} - ${customer.identificacion}`);
    setShowSearchResults(false);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSearchTerm('');
    setFormData({
      ...formData,
      customer_id: '',
      contact_phone: '',
      contact_phone_alt: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.title) {
      setError('Por favor complete los campos requeridos');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await ticketsAPI.create(formData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear el ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Nuevo Ticket de Incidente
        <Typography variant="body2" color="text.secondary">
          Complete los datos del incidente
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* ✅ BUSCADOR DE CLIENTES */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Buscar Cliente
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, cédula, teléfono o email..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchTerm.trim()) {
                    setShowSearchResults(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowSearchResults(false), 200);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearCustomer}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 1 }}
              />
              {showSearchResults && filteredCustomers.length > 0 && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: 250,
                    overflow: 'auto',
                    borderRadius: 1,
                    boxShadow: 3,
                    bgcolor: 'background.paper'
                  }}
                >
                  <List dense>
                    {filteredCustomers.map((customer) => (
                      <ListItem
                        key={customer.id}
                        button
                        onClick={() => handleSelectCustomer(customer)}
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon fontSize="small" color="primary" />
                              <Typography variant="body2" fontWeight="bold">
                                {customer.nombres} {customer.apellidos}
                              </Typography>
                              <Chip
                                label={customer.identificacion}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                              {customer.telefono && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PhoneIcon fontSize="small" color="action" />
                                  <Typography variant="caption">{customer.telefono}</Typography>
                                </Box>
                              )}
                              {customer.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon fontSize="small" color="action" />
                                  <Typography variant="caption">{customer.email}</Typography>
                                </Box>
                              )}
                              {customer.direccion && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <HomeIcon fontSize="small" color="action" />
                                  <Typography variant="caption" noWrap sx={{ maxWidth: 150 }}>
                                    {customer.direccion}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
              {showSearchResults && searchTerm.trim() && filteredCustomers.length === 0 && !loadingCustomers && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    boxShadow: 3
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron clientes. Verifica la búsqueda o crea un nuevo cliente.
                  </Typography>
                </Paper>
              )}
              {selectedCustomer && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  <strong>Cliente seleccionado:</strong> {selectedCustomer.nombres} {selectedCustomer.apellidos}
                  {' - '}
                  <Chip label={selectedCustomer.identificacion} size="small" />
                </Alert>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Título del Incidente"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ej: Corte de servicio, Intermitencia, etc."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción del Incidente"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describa el problema detalladamente..."
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                name="ticket_type"
                value={formData.ticket_type}
                onChange={handleChange}
                label="Tipo"
              >
                <MenuItem value="incidente">Incidente (Falla)</MenuItem>
                <MenuItem value="peticion">Petición (Cambio/Traslado)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Origen del Problema</InputLabel>
              <Select
                name="problem_origin"
                value={formData.problem_origin}
                onChange={handleChange}
                label="Origen del Problema"
              >
                <MenuItem value="corte_total">Corte Total</MenuItem>
                <MenuItem value="intermitencia">Intermitencia</MenuItem>
                <MenuItem value="lentitud">Lentitud / Latencia</MenuItem>
                <MenuItem value="dano_fisico">Daño Físico</MenuItem>
                <MenuItem value="configuracion">Configuración</MenuItem>
                <MenuItem value="otro">Otro</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Prioridad</InputLabel>
              <Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                label="Prioridad"
              >
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="media">Media</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Teléfono Contacto"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Teléfono Alternativo"
              name="contact_phone_alt"
              value={formData.contact_phone_alt}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.customer_id || !formData.title}
        >
          {loading ? <CircularProgress size={24} /> : 'Crear Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
